import { useEffect, useMemo, useRef, useState } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';
import { PlayPauseControls } from '@/libraries/design/components/play-pause-controls/src/play-pause-controls';
import { ColumnLayout } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useWacRefresh } from '@/utils/wac-refresh/wac-refresh-context';

// Per GWP-11501 design spec:
// - All logos render in a 32px-tall container at every breakpoint.
// - Default max width is 140px; logos that need more horizontal room (e.g.
//   HarperCollins) can opt into the wider 240px slot via `wider: true`.
//
// Note (per Dan's dev notes on GWP-11501):
// - Items #6 (remove play/pause for reduced motion) and #7 (padding rules
//   when the button is removed) are deferred — there is an ongoing Slack
//   discussion about the right reduced-motion treatment, so we keep the
//   existing PlayPauseControls behavior.
// - Item #8 (N100 background option) is handled at the Uniform
//   `blockContainer` level (which already exposes Neutral Light 100), not
//   on the Logos pattern itself.
const LOGO_HEIGHT_PX = 32;
const LOGO_MAX_WIDTH_PX = 140;
const LOGO_WIDER_MAX_WIDTH_PX = 240;

export interface Logo {
	src: string;
	alt: string;
	width?: number;
	heightPercentage?: number;
	/**
	 * When true, allows this logo to expand up to 240px wide instead of the
	 * default 140px. Use sparingly for logos with extreme aspect ratios
	 * (e.g. HarperCollins).
	 */
	wider?: boolean;
}

export interface LogosProps {
	title?: string;
	logos: Logo[];
}

/**
 * Measures the logos list and computes:
 * - `maxWidth`: a pixel budget to apply to the <ul> so flex-wrap distributes
 *   logos into evenly-sized rows (same row count, balanced widths).
 * - `overflowsViewport`: true when all logos can't fit on one row, meaning the
 *   marquee should be shown instead.
 */
function useBalancedMaxWidth(logoCount: number): {
	maxWidth: string | undefined;
	overflowsViewport: boolean;
	wrapperRef: { current: HTMLDivElement | null };
	listRef: { current: HTMLUListElement | null };
} {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [measured, setMeasured] = useState<{ widths: number[]; gap: number }>({
		widths: [],
		gap: 0,
	});
	// Mirrors the last overflowsViewport result. Used to add a buffer when
	// switching back from marquee to logos — see the useMemo below.
	const overflowsViewportRef = useRef(false);

	// Watch the wrapper div width. We observe the wrapper (not the <ul>) so
	// that applying maxWidth to the <ul> doesn't trigger the observer again.
	useEffect(() => {
		const el = wrapperRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			// Ignore zero-width reports — the wrapper can briefly report zero
			// when hidden. Keep the last real width so overflowsViewport stays stable.
			if (entry && entry.contentRect.width > 0) {
				setContainerWidth(entry.contentRect.width);
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	// Measure each logo's natural width and the gap between them.
	// Temporarily removes maxWidth and forces nowrap so all items are on one
	// row, giving accurate individual widths and a correct gap reading.
	useEffect(() => {
		const ul = listRef.current;
		if (!ul) return;

		const measure = () => {
			const prevMaxWidth = ul.style.maxWidth;
			const prevMargin = ul.style.margin;
			const prevFlexWrap = ul.style.flexWrap;
			ul.style.maxWidth = 'none';
			ul.style.margin = '0';
			// Force all items onto one row so the gap between adjacent items
			// can be measured. The CSS class sets width:100% which overrides
			// width:max-content, so we use flexWrap:nowrap instead.
			ul.style.flexWrap = 'nowrap';

			try {
				const items = Array.from(ul.querySelectorAll(':scope > li')) as HTMLElement[];
				const widths = items.map((el) => el.getBoundingClientRect().width);

				// Skip if all widths are zero — the list is hidden and not giving real values.
				if (widths.length > 0 && widths.every((w) => w === 0)) return;

				// Detect same-row items by vertical overlap rather than exact
				// top equality — alignItems:center means different-height logos
				// have different top values even when on the same row.
				let gap = 0;
				const r0 = items[0]?.getBoundingClientRect();
				const r1 = items[1]?.getBoundingClientRect();
				if (r0 && r1 && r1.top < r0.bottom && r0.top < r1.bottom) {
					gap = r1.left - r0.right;
				}

				setMeasured({ widths, gap });
			} finally {
				ul.style.maxWidth = prevMaxWidth;
				ul.style.margin = prevMargin;
				ul.style.flexWrap = prevFlexWrap;
			}
		};

		// Don't re-measure while the marquee is showing — we already know logos
		// overflow, and hidden items can return unreliable widths.
		if (overflowsViewportRef.current) return;

		// Wait for images to load before measuring (they affect logo widths).
		let cancelled = false;
		const images = Array.from(ul.querySelectorAll('img'));
		const unloaded = images.filter((img) => !img.complete);
		if (unloaded.length === 0) {
			measure();
		} else {
			Promise.all(
				unloaded.map(
					(img) =>
						new Promise<void>((res) =>
							img.addEventListener('load', () => res(), {
								once: true,
							}),
						),
				),
			).then(() => {
				if (!cancelled) measure();
			});
		}
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [logoCount, containerWidth]);

	const { maxWidth, overflowsViewport } = useMemo(() => {
		const result = (maxWidth: string | undefined, overflowsViewport: boolean) => {
			overflowsViewportRef.current = overflowsViewport;
			return { maxWidth, overflowsViewport };
		};

		const { widths, gap } = measured;
		if (!containerWidth || widths.length === 0) return result(undefined, false);

		const totalWidth = widths.reduce((sum, w) => sum + w, 0) + gap * (widths.length - 1);

		// Everything fits on one row — no constraint needed.
		// Add a one-gap buffer when switching back from marquee to logos to
		// prevent flickering when resizing near the transition point.
		const clearanceNeeded = overflowsViewportRef.current ? gap : 0;
		if (totalWidth <= containerWidth - clearanceNeeded) return result(undefined, false);

		// Greedily split logos into rows given a max row pixel width budget.
		// Returns the pixel width of each row.
		const splitIntoRowWidths = (budget: number): number[] => {
			const rowWidths: number[] = [];
			let rowWidth = 0;
			for (const w of widths) {
				const needed = rowWidth === 0 ? w : gap + w;
				if (rowWidth > 0 && rowWidth + needed > budget) {
					rowWidths.push(rowWidth);
					rowWidth = w;
				} else {
					rowWidth += needed;
				}
			}
			rowWidths.push(rowWidth);
			return rowWidths;
		};

		const currentRowWidths = splitIntoRowWidths(containerWidth);
		const numRows = currentRowWidths.length;
		if (numRows <= 1) return result(undefined, true);

		// Score by the minimum row width — maximising it ensures no row is
		// disproportionately short.
		const score = (rowWidths: number[]) => Math.min(...rowWidths);

		// Candidate budgets: pixel width of every possible contiguous subsequence
		// of logos. These are the only meaningful flex-wrap split points.
		const candidateBudgets = new Set<number>();
		for (let start = 0; start < widths.length; start++) {
			let rowW = 0;
			for (let end = start; end < widths.length; end++) {
				rowW += (widths[end] ?? 0) + (end > start ? gap : 0);
				candidateBudgets.add(rowW);
			}
		}

		let best: string | undefined;
		let bestScore = score(currentRowWidths);

		for (const budget of candidateBudgets) {
			if (budget >= containerWidth) continue;
			const rowWidths = splitIntoRowWidths(budget);
			if (rowWidths.length !== numRows) continue;
			const s = score(rowWidths);
			if (s > bestScore) {
				bestScore = s;
				best = `${budget}px`;
			}
		}

		return result(best, true);
	}, [measured, containerWidth]);

	return { maxWidth, overflowsViewport, wrapperRef, listRef };
}

export const Logos = ({ title, logos }: LogosProps) => {
	const { isDarkTheme } = useTheme();
	const isWacRefresh = useWacRefresh();

	// Declared here (not inside the isWacRefresh block) so hooks are never called conditionally.
	// Default to true (running); correct to false after mount if the user prefers reduced motion.
	// We do this in useEffect (not useState initializer) to avoid SSR/hydration mismatches,
	// since window.matchMedia is not available on the server.
	const [marqueeRunning, setMarqueeRunning] = useState(true);
	useEffect(() => {
		if (
			typeof window !== 'undefined' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		) {
			setMarqueeRunning(false);
		}
	}, []);

	const logoColoringStyles = css({
		filter: isDarkTheme
			? 'brightness(0) invert(1) opacity(.725)' // approximates color.neutral.dark900
			: isWacRefresh // css color filter generator for color.neutral.light900
				? 'brightness(0) saturate(100%) invert(20%) sepia(5%) saturate(755%) hue-rotate(185deg) brightness(98%) contrast(86%)'
				: 'grayscale(100%) brightness(66%)',
	});

	const { maxWidth, overflowsViewport, wrapperRef, listRef } = useBalancedMaxWidth(logos.length);

	const showMarquee = isWacRefresh && overflowsViewport;

	const logosComponent = (
		<div ref={wrapperRef} css={logosWrapperStyle}>
			<ul
				ref={listRef}
				css={[logosContainerStyle, isWacRefresh && wacRefreshSizeStyles]}
				style={maxWidth ? { maxWidth, margin: '0 auto' } : undefined}
			>
				{logos.map((logo, index) => (
					<li key={index} css={[isWacRefresh && marqueeListItemStyles]}>
						<Image
							src={logo.src}
							alt={logo.alt}
							css={[
								regularLogoStyles,
								logoColoringStyles,
								// In the WAC refresh, every logo lives in a
								// 32px-tall row and is constrained to the
								// 140px (or 240px when wider) width slot,
								// scaling down with object-fit.
								isWacRefresh
									? css({
											height: `${LOGO_HEIGHT_PX}px`,
											width: 'auto',
											maxWidth: logo.wider
												? `${LOGO_WIDER_MAX_WIDTH_PX}px`
												: `${LOGO_MAX_WIDTH_PX}px`,
											objectFit: 'contain',
											objectPosition: 'center',
										})
									: logo.heightPercentage
										? css({
												height: `${logo.heightPercentage}%`,
											})
										: logo.width
											? css({
													width: `${logo.width}px`,
													height: 'auto',
												})
											: css({}),
							]}
						/>
					</li>
				))}
			</ul>
		</div>
	);

	if (isWacRefresh) {
		const marqueeLogos = [...logos, ...logos, ...logos, ...logos];
		const originalCount = logos.length;
		return (
			<div
				css={css({
					position: 'relative',
					paddingBlock: token('space.800'),
				})}
			>
				{showMarquee ? (
					<div css={marqueeContainerStyles}>
						<div css={marqueeListContainerStyles}>
							<ul
								css={[
									animationKeyframesStyles,
									listStyles,
									wacRefreshSizeStyles,
									css({
										animation: 'scroll 90s linear infinite',
										animationPlayState: marqueeRunning ? 'running' : 'paused',
									}),
								]}
							>
								{marqueeLogos.map((logo, index) => {
									const isOriginal = index < originalCount;
									return (
										<li aria-hidden={!isOriginal} key={index} css={marqueeListItemStyles}>
											<Image
												src={logo.src}
												alt={logo.alt}
												css={[
													marqueeLogoStyles,
													logoColoringStyles,
													css({
														maxWidth: logo.wider
															? `${LOGO_WIDER_MAX_WIDTH_PX}px`
															: `${LOGO_MAX_WIDTH_PX}px`,
													}),
												]}
											/>
										</li>
									);
								})}
							</ul>
						</div>
						<ColumnLayout layout="12">
							<div css={marqueeControlsContainerStyles}>
								<PlayPauseControls
									isPlaying={marqueeRunning}
									onToggle={() => setMarqueeRunning((r) => !r)}
								/>
							</div>
						</ColumnLayout>
					</div>
				) : (
					logosComponent
				)}
				{/* Hidden measurement div — keeps wrapperRef/listRef in the DOM
                    so the hook can re-measure when switching back to logos. */}
				{showMarquee && (
					<div ref={wrapperRef} css={measurementOnlyStyles} aria-hidden>
						<ul ref={listRef} css={[logosContainerStyle, wacRefreshSizeStyles]}>
							{logos.map((logo, index) => (
								<li key={index} css={marqueeListItemStyles}>
									<Image src={logo.src} alt={logo.alt} css={regularLogoStyles} />
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		);
	}

	return (
		<div
			css={css({
				display: 'flex',
				flexDirection: 'column',
				gap: token('space.600'),
				[media.above.md]: {
					gap: token('space.800'),
				},
				textAlign: 'center',
			})}
		>
			{title && <h3 css={css({ margin: 0 })}>{title}</h3>}
			{logosComponent}
		</div>
	);
};

const regularLogoStyles = css({
	height: '32px',
	width: 'auto',
});

// visibility:hidden (not display:none) so ResizeObserver and
// getBoundingClientRect() still return real dimensions.
const measurementOnlyStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	insetInlineEnd: 0,
	visibility: 'hidden',
});

const logosWrapperStyle = css({
	width: '100%',
});

const logosContainerStyle = css({
	display: 'flex',
	flexWrap: 'wrap',
	width: '100%',
	margin: 0,
	padding: 0,
	listStyle: 'none',
	justifyContent: 'center',
	rowGap: token('space.600'),
	columnGap: token('space.400'),
	[media.above.sm]: {
		columnGap: token('space.800'),
	},
	[media.above.md]: {
		rowGap: token('space.800'),
	},
	alignItems: 'center',
});

const marqueeLogoStyles = css({
	height: '100%',
	width: 'fit-content',
	objectFit: 'contain',
	objectPosition: 'center',
});

const marqueeListItemStyles = css({
	height: '100%',
	display: 'flex',
	alignItems: 'center',
});

const listStyles = css({
	listStyle: 'none',
	padding: 0,
	margin: 0,
	display: 'flex',
	width: 'fit-content',
});

// Per GWP-11501 (#1, #2, #3, #9): a single 32px-tall row with 80px between
// logos at every breakpoint.
//
// The columnGap overrides are repeated at the same breakpoints used in
// logosContainerStyle so that they win at every viewport size without
// relying on !important. CSS specificity for a media-query rule is equal
// to the same rule outside a media query, so the last declaration in source
// order wins — and since wacRefreshSizeStyles is applied after
// logosContainerStyle in the css array, these values take effect correctly.
const wacRefreshSizeStyles = css({
	columnGap: token('space.1000'),
	height: `${LOGO_HEIGHT_PX}px`,
	[media.above.sm]: {
		columnGap: token('space.1000'),
	},
	[media.above.md]: {
		columnGap: token('space.1000'),
		rowGap: 0,
	},
});

const marqueeContainerStyles = css({
	overflow: 'hidden',
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
});

const marqueeListContainerStyles = css({
	width: '100%',
	maxWidth: '4000px',
	margin: 'auto',
	overflowX: 'hidden',
});

const animationKeyframesStyles = css({
	width: '100%',
	whiteSpace: 'nowrap',

	['@keyframes scroll']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: { transform: 'translateX(0%)' },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: { transform: 'translateX(calc(-50% - 10px))' },
	},
});

const marqueeControlsContainerStyles = css({
	width: '100%',
	display: 'flex',
	justifyContent: 'end',
});
